import { useNavigate } from "react-router-dom";
import { getConfig } from "../config";
import { getUserManager } from "../auth/userManager";
import { asError, eventBus } from "@optimyl/events";
import { BrandHeader } from "../components/BrandHeader";

/**
 * Test-only page that lets the user pick which Salesforce environment
 * to authenticate against. When auth is enabled, selecting an env triggers
 * a fresh login with prompt=login. When auth is disabled, it just navigates
 * to the form.
 */
export function EnvPickerPage() {
  const navigate = useNavigate();
  const config = getConfig();
  const environments = config.AVAILABLE_SF_ENVIRONMENTS ?? [];

  const handleSelect = (env: string) => {
    sessionStorage.setItem("sfEnv", env);
    const next = new URLSearchParams(window.location.search).get("next");

    if (!config.AUTH_ENABLED) {
      navigate(next || "/intake", { replace: true });
      return;
    }

    // Preserve the original deep-link URL through the OIDC round-trip
    if (next) sessionStorage.setItem("preLoginUrl", next);

    // Use signinRedirect with prompt=login to force fresh authentication.
    // PKCE (code_challenge) is generated automatically by oidc-client-ts.
    getUserManager().signinRedirect({
      prompt: "login",
      extraQueryParams: { sf_env: env },
    }).catch((err) => {
      eventBus.publish("error", {
        error: asError(err),
        context: "EnvPickerPage.signinRedirect",
      });
    });
  };

  return (
    <div className="min-h-[100dvh] flex items-start md:items-center justify-center bg-gray-50 px-4 pt-16 md:pt-0">
      <div className="max-w-sm w-full bg-white rounded-xl shadow-md p-8">
        <BrandHeader />

        <p className="text-sm text-gray-600 mb-4 text-center">
          Select which Salesforce environment to connect to for this session.
        </p>

        <div className="flex flex-col gap-3">
          {environments.map((env) => (
            <button
              key={env}
              onClick={() => handleSelect(env)}
              className="w-full rounded-lg bg-brand-green px-6 py-3 text-white font-medium hover:bg-brand-green-hover transition-colors cursor-pointer"
            >
              {env.toUpperCase()}
            </button>
          ))}
        </div>

        {environments.length === 0 && (
          <p className="text-sm text-gray-400 text-center">
            No environments configured. Set AVAILABLE_SF_ENVIRONMENTS in config.json.
          </p>
        )}

        <p className="text-xs text-gray-400 text-center mt-4">
          This page is only shown in test deployments.
        </p>
      </div>
    </div>
  );
}
